/**
 * Reserveringskosten per deal (Dennis 24-10-2026, regiesessie Google Ads
 * kerstmarkten, op "geen boekingskosten met € 49 p.p. alles inbegrepen":
 * "doen").
 *
 * De backend geeft per deal en per prijsopgave `reservation_fee_eur` mee: wat
 * een gewone boeking van die deal aan reserveringskosten rekent (0 of 19).
 * Bij 0 zit alles al in de prijs per persoon; de site toont dan geen
 * reserveringskostenregel en telt nooit zelf € 19 op.
 *
 * Ontbreekt het veld (oude backend), dan valt alles terug op de vaste
 * RESERVATION_FEE_EUR: precies het gedrag van vóór dit besluit.
 *
 * JSX-vrij en los van format-price.js, zodat componenttests die
 * "@/lib/format-price" mocken deze helpers ongemoeid laten. De terugval komt
 * daarom als verplicht argument binnen: de componenten geven hun eigen
 * (eventueel gemockte) RESERVATION_FEE_EUR uit format-price door. Dit bestand
 * importeert bewust niets, zodat een gemockte price-math of format-price het
 * niet raakt.
 */

/**
 * Het expliciete `reservation_fee_eur` van één bron (deal of prijsopgave), of
 * null als het ontbreekt of onbruikbaar is (negatief, geen getal, leeg).
 */
export function readReservationFeeEur(source) {
  if (!source || typeof source !== "object") return null;
  const raw = source.reservation_fee_eur;
  if (typeof raw !== "number" && typeof raw !== "string") return null;
  if (typeof raw === "string" && raw.trim() === "") return null;
  const fee = Number(raw);
  return Number.isFinite(fee) && fee >= 0 ? fee : null;
}

/**
 * De reserveringskosten die gelden: het eerste expliciete veld uit `sources`
 * (volgorde = voorrang, bv. [prijsopgave, deal]), anders `fallback` (de vaste
 * RESERVATION_FEE_EUR van de aanroeper).
 */
export function resolveReservationFeeEur(sources, fallback) {
  const list = Array.isArray(sources) ? sources : [sources];
  for (const source of list) {
    const fee = readReservationFeeEur(source);
    if (fee !== null) return fee;
  }
  return fallback;
}

/**
 * Alles inbegrepen: de backend zegt expliciet 0. Een ontbrekend veld is
 * nooit "alles inbegrepen" (oude backend = huidig beeld).
 */
export function isAllInclusiveReservationFee(sources) {
  const list = Array.isArray(sources) ? sources : [sources];
  for (const source of list) {
    const fee = readReservationFeeEur(source);
    if (fee !== null) return fee === 0;
  }
  return false;
}

// Strikt geldbedrag (zelfde regel als booking/non-negative-price.js, hier
// herhaald omdat dit bestand bewust niets importeert): een getal of een
// niet-lege numerieke string, eindig en >= 0; anders null.
function readAmount(raw) {
  if (typeof raw !== "number" && typeof raw !== "string") return null;
  if (typeof raw === "string" && raw.trim() === "") return null;
  const value = Number(raw);
  return Number.isFinite(value) && value >= 0 ? value : null;
}

/**
 * Vervolg-PR op fe#1722 (punt 4), fixronde 2 op fe#1731: DE reserveringskosten
 * voor de lokale terugvalregels van een boekscherm. Zijbalk (SidePriceInfo),
 * reserveringsformulier (ReservationForm) en mobiele prijsbalk (BookingWizard)
 * lezen alle drie deze ene helper, zodat ze bij elke opgave hetzelfde bedrag
 * tonen en versturen.
 *
 * Het schakelaar-aan-signaal is uitsluitend een expliciet
 * `reservation_fee_eur: 0` (opgave vóór deal; het veld dat fe#1722 in deze
 * module invoerde, zie readReservationFeeEur). Zonder dat signaal (veld 19
 * of afwezig) is de uitkomst exact de helper van fe#1722/main:
 * `resolveReservationFeeEur([quote, deal], fallback)`.
 * Een `reservationFee: 0` zonder het kerstveld verandert dan niets.
 *
 * Met het signaal wint het bedrag dat de opgave zelf rekent (`reservationFee`),
 * zodat een opgave met `reservation_fee_eur: 0` maar `reservationFee: 19` ook
 * lokaal € 19 toont. Zonder dat bedrag 0: geen kostenregel die niet uit de
 * opgave komt.
 */
export function localReservationFeeEur(quote, deal, fallback) {
  const fee = resolveReservationFeeEur([quote, deal], fallback);
  if (fee !== 0 || !isAllInclusiveReservationFee([quote, deal])) return fee;
  const opgaveFee =
    quote && typeof quote === "object" ? readAmount(quote.reservationFee) : null;
  return opgaveFee ?? 0;
}

/**
 * Fixronde 5 op fe#1722: rekent de actuele prijsopgave zelf expliciet
 * `reservationFee` 0? Alleen dan mag een boekscherm (zijbalk, kamerstap,
 * reserveringsformulier) "geen reserveringskosten" zeggen. Een opgave zonder
 * het veld, met een onbruikbaar veld of met kosten: false; dan toont het
 * scherm de kosten zoals op main.
 *
 * De frontend rekent nooit zelf een bedrag uit een opgave na (de eerdere
 * `withoutReservationFeeWhenAllInclusive` haalde € 19 van `total` af zodra
 * `reservation_fee_eur` 0 was, ook als de opgave zelf `reservationFee: 19`
 * rekende: 98 op het scherm en in de pre-reservering, 117 bij de backend,
 * AMOUNT_MISMATCH bij de kassa). Elk bedrag komt ruw uit de opgave.
 */
export function quoteReportsNoReservationFee(quote) {
  if (!quote || typeof quote !== "object" || Array.isArray(quote)) return false;
  return readAmount(quote.reservationFee) === 0;
}
